import * as Styles from "../../../styles.ts";
import { css, html, LitElement, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { map } from "lit/directives/map.js";

// Components
import "../../dui/toolbar/dui-toolbar.ts";
import "../../dui/button/dui-button.ts";
import "../../dui/icon/dui-icon.ts";
import "../../dui/menu/dui-menu.ts";
import "../../dui/tabs/dui-tabs.ts";
import "../../dui/tabs/dui-tabs-list.ts";
import "../../dui/tabs/dui-tab.ts";
import "../../dui/tabs/dui-tabs-indicator.ts";
import { customEvent } from "../../../util/event.ts";
import { restartAnimation } from "../../../util/dom.ts";
import { type CanvasViewKey } from "../../app-store.ts";

const canvasViewLabels: ReadonlyArray<{ key: CanvasViewKey; label: string }> = [
  { key: "project-info", label: "Project" },
  { key: "scan-hits", label: "Scan Hits" },
  { key: "force-table", label: "Forces" },
  { key: "force-vis", label: "Connections" },
  { key: "force-map", label: "Map" },
  { key: "report", label: "Report" },
];

const style = css`
  :host {
    --inset-x: var(--space-4);
    --inset-y: var(--space-4);
    display: block;
    height: 100cqh;
    width: 100cqw;
    container-type: size;
  }

  .panel {
    background-color: var(--card);
    height: 100cqh;
    container-type: size;
  }

  .panel-inner {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    height: 100cqh;
  }

  .panel-title {
    font-weight: 600;
  }

  @keyframes panel-enter {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .panel-main {
    animation: panel-enter 400ms ease-in-out forwards;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-color: var(--muted-foreground) transparent;
    scrollbar-width: thin;
    container-type: size;
  }

  :host([no-close]) .panel-close {
    display: none;
  }

  :host([inset]) .panel-main {
    padding: var(--inset-y) var(--inset-x);
  }

  dui-toolbar dui-tabs {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  dui-toolbar dui-tabs-list::part(list) {
    justify-content: center;
    overflow-x: auto;
    scrollbar-width: none;
    box-shadow: none;
  }
`;

const canvasClose = customEvent<void>("canvas-close", {
  bubbles: true,
  composed: true,
});

const canvasViewChange = customEvent<CanvasViewKey>("canvas-view-change", {
  bubbles: true,
  composed: true,
});

@customElement("canvas-panel")
export class CanvasPanelElement extends LitElement {
  static override styles = [Styles.base, style];

  @property({ type: String, attribute: "canvas-view" })
  accessor canvasView: CanvasViewKey | undefined = undefined;

  #showTabs = (): boolean =>
    this.canvasView !== undefined && this.canvasView !== "force-edit";

  protected override updated(changed: PropertyValues): void {
    // Re-trigger panel-enter animation when switching between tabbed views
    if (changed.has("canvasView") && changed.get("canvasView") !== undefined) {
      const panelMain = this.shadowRoot?.querySelector(".panel-main") as
        | HTMLElement
        | undefined;
      if (panelMain) {
        restartAnimation(panelMain);
      }
    }
  }

  #onClose = () => {
    this.dispatchEvent(canvasClose());
  };

  #onTabChange = (event: CustomEvent<CanvasViewKey>): void => {
    this.dispatchEvent(canvasViewChange(event.detail));
  };

  override render() {
    const showTabs = this.#showTabs();
    return html`
      <section class="panel">
        <div class="panel-inner">
          <dui-toolbar
            inset
            has-button-left
            size="md"
            ?expand-center="${showTabs}"
          >
            <dui-button
              class="panel-close"
              slot="left"
              variant="ghost"
              square
              @click="${this.#onClose}"
              ><dui-icon icon="close"></dui-icon
            ></dui-button>
            ${showTabs
              ? html` <dui-tabs
                  slot="center"
                  .value="${this.canvasView}"
                  @value-change="${this.#onTabChange}"
                >
                  <dui-tabs-list>
                    ${map(
                      canvasViewLabels,
                      (v) =>
                        html`<dui-tab value="${v.key}">${v.label}</dui-tab>`,
                    )}
                    <dui-tabs-indicator></dui-tabs-indicator>
                  </dui-tabs-list>
                </dui-tabs>`
              : html`<div class="panel-title" slot="center">
                  <slot name="title"></slot>
                </div>`}
          </dui-toolbar>
          <div class="panel-main">
            <slot></slot>
          </div>
        </div>
        <slot name="overlay"></slot>
      </section>
    `;
  }
}
